import {
  type AttributeValueType,
  addSignalAttribute,
  identify,
  init,
  removeSignalAttribute,
  reportError,
} from '@dash0/sdk-web';
import { isAxiosError, isCancel } from 'axios';

import { getRequestDetails } from '@/api/requestDetails';
import appConfig from '@/config';

type ReportableErrorType = {
  name?: string;
  stack?: string;
  errors?: unknown[];
};

type ReportOptionsType = {
  message: string;
  stack?: string;
  source:
    | 'captureException'
    | 'vue-error-handler'
    | 'window-error'
    | 'unhandled-rejection';
  attributes?: Record<string, AttributeValueType | undefined>;
};

const ignoredStatuses = new Set([401, 403, 404]);

const reportedErrors = new WeakSet<object>();

const maxReportsPerPage = 50;

const uncaughtSources = new Set(['window-error', 'unhandled-rejection']);

const extensionFramePattern = /(chrome|moz|safari(-web)?)-extension:\/\//;
let reportCount = 0;

const ignoredErrorMessages = [
  /__firefox__/,
  /window\.ethereum/,
  /MetaMask/,
  /Extension context invalidated/,
  /Receiving end does not exist/,
  /Object Not Found Matching Id/,
  /^Script error\.?$/,
];

export function initDash0() {
  if (appConfig.dash0) {
    const { propagators, ...dash0Config } = appConfig.dash0;
    init({
      ...dash0Config,
      propagators: propagators?.map(({ type, match }) => ({
        type,
        match: [...match],
      })),
      serviceVersion: APP_VERSION,
      ignoreErrorMessages: ignoredErrorMessages,
      enabledInstrumentations: [
        '@dash0/navigation',
        '@dash0/web-vitals',
        '@dash0/fetch',
      ],
    });
    window.addEventListener('error', reportWindowError);
    window.addEventListener('unhandledrejection', reportUnhandledRejection);
  }
}

function reportWindowError(event: ErrorEvent) {
  reportDash0Error(event.error ?? event.message, {
    message: event.message || String(event.error),
    source: 'window-error',
  });
}

function reportUnhandledRejection(event: PromiseRejectionEvent) {
  const reason: unknown = event.reason;
  const reasonErrors = responseErrors(reason);
  const reasonMessage =
    reason instanceof Error
      ? reason.message
      : reasonErrors
        ? reasonErrors.join(', ')
        : String(reason ?? 'no reason');
  reportDash0Error(reason, {
    message: `Unhandled promise rejection: ${reasonMessage}`,
    source: 'unhandled-rejection',
  });
}

export function identifyDash0User({
  userId,
  email,
  fullName,
}: {
  userId?: number;
  email?: string;
  fullName?: string;
}) {
  if (appConfig.dash0 && userId) {
    identify(String(userId), { email, fullName });
  }
}

export function setDash0Organization({
  id,
  uuid,
  name,
}: {
  id?: number;
  uuid?: string;
  name?: string;
}) {
  if (!appConfig.dash0) return;
  const attributes = {
    'crossbeam.organization.id': id,
    'crossbeam.organization.uuid': uuid,
    'crossbeam.organization.name': name,
  };
  Object.entries(attributes).forEach(([key, value]) => {
    removeSignalAttribute(key);
    if (value != null) addSignalAttribute(key, value);
  });
}

function withNamedAsyncFrames(stack?: string) {
  return stack?.replace(
    /^(\s*at async )(https?:\/\/\S+:\d+:\d+)$/gm,
    '$1<anonymous> ($2)',
  );
}

function requestPath(url: string) {
  return url
    .replace(/\?.*$/, '')
    .split('/')
    .map((segment) =>
      /^v\d+(\.\d+)?$/.test(segment) || !/[@\dA-Z]/.test(segment)
        ? segment
        : '{id}',
    )
    .join('/');
}

function requestDetails(error: unknown) {
  const details = getRequestDetails(error);
  if (details) {
    return { ...details, pathTemplate: details.path };
  }
  if (!isAxiosError(error) || !error.config?.url) return undefined;
  return {
    method: error.config.method?.toUpperCase() ?? 'REQUEST',
    pathTemplate: requestPath(error.config.url),
    status: error.response?.status,
  };
}

function responseErrors(error: unknown) {
  const errors = isAxiosError<{ errors?: unknown }>(error)
    ? error.response?.data?.errors
    : (error as ReportableErrorType | undefined)?.errors;
  return Array.isArray(errors) ? errors : undefined;
}

function isFromExtension(stack?: string) {
  const firstFrame = stack
    ?.split('\n')
    .find((line) => /^\s*at\s/.test(line) || /@\S+:\d+:\d+$/.test(line));
  return !!firstFrame && extensionFramePattern.test(firstFrame);
}

function isIgnoredError(error: unknown, status?: number) {
  if (status && ignoredStatuses.has(status)) return true;
  if (isCancel(error)) return true;
  return error instanceof DOMException && error.name === 'AbortError';
}

export function reportDash0Error(
  error: unknown,
  { message, stack, source, attributes = {} }: ReportOptionsType,
) {
  if (!appConfig.dash0) return;
  const request = requestDetails(error);
  if (isIgnoredError(error, request?.status)) return;
  if (error && typeof error === 'object') {
    if (reportedErrors.has(error)) return;
    reportedErrors.add(error);
  }
  const err: ReportableErrorType =
    error && typeof error === 'object' ? error : {};
  if (isFromExtension(err.stack)) return;
  if (!uncaughtSources.has(source)) {
    if (reportCount >= maxReportsPerPage) return;
    reportCount += 1;
  }
  const requestStack =
    request && 'stack' in request ? request.stack : undefined;
  const allAttributes: Record<string, AttributeValueType | undefined> = {
    'crossbeam.error.source': source,
    'http.request.method': request?.method,
    'crossbeam.request.template': request?.pathTemplate,
    'http.response.status_code': request?.status,
    'crossbeam.response.errors': responseErrors(error)
      ?.slice(0, 10)
      .map((item) => String(item).slice(0, 300)),
    'crossbeam.response.text':
      request && 'responseText' in request ? request.responseText : undefined,
    'crossbeam.capture.stacktrace':
      !err.stack && requestStack ? withNamedAsyncFrames(stack) : undefined,
    ...attributes,
  };
  reportError(
    {
      name: err.name ?? (requestStack ? 'ApiError' : undefined),
      message: request
        ? `${request.method} ${request.pathTemplate}: ${request.status ?? message}`
        : message,
      stack: withNamedAsyncFrames(err.stack || requestStack || stack),
    },
    {
      attributes: Object.fromEntries(
        Object.entries(allAttributes).filter(
          (entry): entry is [string, AttributeValueType] => entry[1] != null,
        ),
      ),
    },
  );
}
